// src/components/dashboard/Dashboard.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useUser } from "../../contexts/UserContext";
import { useAIWorkflow } from "../../contexts/AIWorkflowContext";
import jobService from "../../services/jobService"; // For resume uploads

// --- Layout Components & Widgets ---
import Sidebar from "./Sidebar/Sidebar";
import Header from "./Header/Header";
// --- RIGHT SIDEBAR WIDGETS (COMMENTED OUT) ---
// import ResumeUploader from "./widgets/ResumeUploader";
// import CoursesSection from "./CoursesSection/CoursesSection";
// import CourseDetailsModal from "./CoursesSection/CourseDetailsModal";

// --- Mobile Navigation Component ---
import MobileNavBar from "./MobileNavBar/MobileNavBar";

// --- CSS Imports ---
import "./Dashboard.css";
import "./widgets/Widgets.css";
import "./ai_flow/aiFlowStyles.css";
import "./jobs/JobMatcher.css";
import "./jobs/JobDetails.css";
import "./CoursesSection/CourseStyles.css";
import "../../styles/aiMatchmakingMobile.css"; // ADD THIS IMPORT FOR MOBILE STYLES

const Dashboard = () => {
  const { logout, currentUser } = useAuth();
  const { userData, loading: userLoading, error: userError } = useUser(); // Get user details and loading state
  const {
    // --- RIGHT SIDEBAR DATA (COMMENTED OUT) ---
    // recommendedCourses,
    // isLoadingCourses,
    // coursesError,
    incrementResumeListVersion,
  } = useAIWorkflow();
  const navigate = useNavigate();

  // --- Local State ---
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  // --- RIGHT SIDEBAR STATE (COMMENTED OUT) ---
  // const [showCourseModal, setShowCourseModal] = useState(false);
  // const [courseForModal, setCourseForModal] = useState(null);

  // --- ADD MOBILE STATE ---
  const [isMobile, setIsMobile] = useState(false);

  // --- User Info for Header ---
  // Prioritize fullName from detailed profile (userData), fallback to email part from currentUser
  const derivedFullName =
    userData?.fullName || currentUser?.email?.split("@")[0] || "User";
  const derivedUserEmail = userData?.email || currentUser?.email || "";
  // Ensure profilePicture is handled gracefully if userData is null/undefined initially
  const derivedProfilePicture = userData?.profilePicture || "";

  // --- Apply Body Class ---
  useEffect(() => {
    document.body.classList.add("smai-dashboard-body");
    return () => document.body.classList.remove("smai-dashboard-body");
  }, []);

  // --- ADD MOBILE CHECK EFFECT ---
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // --- Logout Handler ---
  const handleLogout = async () => {
    if (isLoggingOut) return; // Prevent multiple logout attempts
    setIsLoggingOut(true);
    try {
      await logout(); // Call logout from AuthContext
      navigate("/login"); // Redirect to login after successful logout
    } catch (error) {
      console.error("Logout error:", error);
      // Still try to navigate to login even if backend logout fails,
      // as the local auth state might be cleared by AuthContext.
      navigate("/login");
    } finally {
      setIsLoggingOut(false);
    }
  };

  // --- RIGHT SIDEBAR HANDLERS (COMMENTED OUT) ---
  /*
  const handleShowCourseModal = (course) => {
    setCourseForModal(course);
    setShowCourseModal(true);
  };
  const handleCloseCourseModal = () => {
    setShowCourseModal(false);
    setCourseForModal(null);
  };
  */

  // --- Resume Upload Handler (for Sidebar Widget - COMMENTED OUT) ---
  /*
  const handleResumeUpload = useCallback(
    async (file, title) => {
      console.log(`[Dashboard] Handling resume upload: ${title}`);
      try {
        const savedResume = await jobService.saveResumeFile(file, title, false);
        if (savedResume && savedResume.id) {
          console.log(
            `[Dashboard] Resume saved successfully: ID ${savedResume.id}`
          );
          incrementResumeListVersion();
          return true;
        } else {
          throw new Error("Failed to save resume. Invalid API response.");
        }
      } catch (err) {
        console.error("[Dashboard] Resume upload failed:", err);
        throw err;
      }
    },
    [incrementResumeListVersion]
  );
  */

  // --- Render ---
  if (userError) {
    // Handle user data fetching error (e.g., redirect to login or show error message)
    console.error("Error fetching user data:", userError);
    // Example: navigate('/login'); or return <p>Error loading user data.</p>;
  }

  return (
    <div
      className={`smai-dashboard-container smai-dashboard-layout-main ${
        isMobile ? "has-mobile-nav" : ""
      }`}
    >
      {/* Sidebar */}
      <Sidebar onLogout={handleLogout} isLoggingOut={isLoggingOut} />

      {/* Header */}
      <Header
        fullName={derivedFullName}
        userEmail={derivedUserEmail}
        profilePicture={derivedProfilePicture}
        isLoading={userLoading}
        onLogout={handleLogout}
      />

      {/* Mobile Navigation Bar - only shows on mobile */}
      {isMobile && <MobileNavBar />}

      {/* Main Content Area (where nested routes render) */}
      <div className="smai-main-content-area">
        <Outlet /> {/* Child route components render here */}
      </div>

      {/* --- RIGHT SIDEBAR WIDGETS AREA (COMMENTED OUT) --- */}
      {/*
      <div className="smai-right-sidebar-area">
        <ResumeUploader onUploadSubmit={handleResumeUpload} />

        <div className="smai-widget smai-recommended-courses-widget widget-card">
          <h3 className="smai-widget-title">
            <span className="smai-title-dot red"></span>
            Recommended Courses
          </h3>
          <div className="smai-widget-content">
            {isLoadingCourses && (
              <p className="smai-loading-text">Loading recommendations...</p>
            )}
            {coursesError && (
              <p className="smai-error-text">Error: {coursesError}</p>
            )}
            {!isLoadingCourses &&
              !coursesError &&
              recommendedCourses.length > 0 && (
                <CoursesSection
                  courses={recommendedCourses}
                  layout="compact"
                  title=""
                  onViewCourseDetails={handleShowCourseModal}
                />
              )}
            {!isLoadingCourses &&
              !coursesError &&
              recommendedCourses.length === 0 && (
                <p className="smai-empty-text">
                  No recommendations yet. Analyze a profile first.
                </p>
              )}
          </div>
        </div>
      </div>
      */}

      {/* --- Course Details Modal (COMMENTED OUT) --- */}
      {/*
      {showCourseModal && courseForModal && (
        <CourseDetailsModal
          course={courseForModal}
          onClose={handleCloseCourseModal}
        />
      )}
      */}
    </div>
  );
};

export default Dashboard;
